import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { RISK_LEVELS, totalBand } from "./riskConstants";
import { submitRiskAssessment } from "../../lib/api";

const scoresFromExisting = (existing, categories) => {
  const src = existing?.scores || {};
  const out = {};
  categories.forEach((c) => { out[c.key] = src[c.key] ?? null; });
  return out;
};

export default function RiskAssessmentDialog({ open, onClose, onSaved, week, existing, categories = [] }) {
  const [scores, setScores] = useState({});
  const [comments, setComments] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setScores(scoresFromExisting(existing, categories));
      setComments(existing?.comments || "");
    }
  }, [open, existing, categories]);

  const maxTotal = categories.length * 5;
  const total = categories.reduce((sum, c) => sum + (scores[c.key] || 0), 0);
  const band = totalBand(total);
  const allFilled = categories.length > 0 && categories.every((c) => scores[c.key] != null);

  const submit = async () => {
    if (!allFilled) {
      toast.error("Please score every question");
      return;
    }
    setSaving(true);
    try {
      await submitRiskAssessment({ week, scores, comments });
      toast.success(existing ? "Risk assessment updated" : "Risk assessment submitted");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save assessment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="risk-assessment-dialog">
        <DialogHeader>
          <DialogTitle>Weekly Risk Assessment — Week ending {week}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div className="px-4 py-3 rounded-lg border bg-gray-50 flex items-center justify-between flex-wrap gap-3">
            <p className="text-sm text-gray-600 flex-1 min-w-[200px]">
              Score the overall vibe of the student population this week — not any individual student.
            </p>
            <div>
              <div className="text-xs text-gray-500">Your Total</div>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold" data-testid="risk-total">{total}</span>
                <span className="text-xs text-gray-500">/ {maxTotal}</span>
                {band && total > 0 && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${band.color}`}>
                    {band.label}
                  </span>
                )}
              </div>
            </div>
          </div>

          {categories.map((cat) => {
            const selected = scores[cat.key];
            return (
              <div key={cat.key} className="border rounded-lg p-4 space-y-3">
                <div className="font-semibold text-[#00305E]">{cat.label}</div>
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                  {RISK_LEVELS.map((lvl) => {
                    const isActive = selected === lvl.value;
                    return (
                      <button
                        key={lvl.value}
                        type="button"
                        onClick={() => setScores((s) => ({ ...s, [cat.key]: lvl.value }))}
                        className={`text-left p-2 rounded-md border text-xs transition-all ${
                          isActive
                            ? `${lvl.color} border-current shadow-sm font-medium ring-2 ring-offset-1 ring-current`
                            : "bg-white hover:bg-gray-50 text-gray-700 border-gray-200"
                        }`}
                        data-testid={`risk-score-${cat.key}-${lvl.value}`}
                      >
                        <div className="font-semibold">{lvl.value} — {lvl.label}</div>
                        <div className="text-[10px] mt-1 line-clamp-3 opacity-80">
                          {(cat.descriptions || {})[lvl.value] || (cat.descriptions || {})[String(lvl.value)]}
                        </div>
                      </button>
                    );
                  })}
                </div>
                {selected != null && (
                  <button
                    type="button"
                    onClick={() => setScores((s) => ({ ...s, [cat.key]: null }))}
                    className="text-xs text-gray-500 hover:text-gray-700 underline"
                  >
                    Clear
                  </button>
                )}
              </div>
            );
          })}

          <div>
            <Label htmlFor="risk-comments">Comments (optional)</Label>
            <Textarea
              id="risk-comments"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Anything noteworthy this week..."
              className="min-h-[80px]"
              data-testid="risk-comments-input"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !allFilled} data-testid="risk-save-btn">
            {saving ? "Saving..." : existing ? "Update" : "Submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
